/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';

import { request } from '@/api/client';
import { DEFAULT_RANGE, PRESET_KEYS } from '@/lib/ranges';

/**
 * Which days the dashboard reports on: a preset, or two dates of the person's
 * own.
 *
 * A value this build cannot read -- a preset it does not know, or nothing at
 * all from an API a release behind -- reads as the default rather than
 * failing every screen that reads a preference. The server does the same with
 * what it stores.
 */
export const dashboardRangeSchema = z
  .object({
    preset: z.enum(PRESET_KEYS),
    from: z.string().optional(),
    to: z.string().optional(),
  })
  .catch({ ...DEFAULT_RANGE });

/**
 * What each person has arranged for themselves.
 *
 * Kept with the person, not with the browser: sign in from another machine and
 * it is the same Vellum. The one copy that lives in the browser is the theme,
 * because the sign-in screen paints before anybody is authenticated.
 */
export const preferencesSchema = z.object({
  theme: z.enum(['light', 'dark']),
  rail: z.enum(['full', 'mini']),
  dashboard_cards: z.array(z.string()),
  // Kept for the API's sake; the dashboard now follows `dashboard_range`.
  dashboard_period: z.enum(['week', 'month', 'quarter']),
  dashboard_range: dashboardRangeSchema,
  table_pages: z.record(z.string(), z.number()),
  agreements_view: z.string(),
  agreements_filters: z.record(z.string(), z.string()),
});

export type Preferences = z.infer<typeof preferencesSchema>;
export type Period = Preferences['dashboard_period'];

/**
 * What the interface falls back to before the server has answered.
 *
 * The same values the server would send, so the first paint and the second do
 * not disagree with each other.
 */
export const FALLBACK: Preferences = {
  theme: 'light',
  rail: 'full',
  dashboard_cards: ['quarter', 'team', 'cycle', 'dept'],
  dashboard_period: 'month',
  dashboard_range: DEFAULT_RANGE,
  table_pages: {},
  agreements_view: 'all',
  agreements_filters: {},
};

export const preferencesQuery = () =>
  queryOptions({
    queryKey: ['preferences'] as const,
    queryFn: ({ signal }) =>
      request('/api/v1/me/preferences', preferencesSchema, { signal }),
    // Preferences change when this person changes them and at no other time,
    // so refetching them on every window focus is pure noise.
    staleTime: Infinity,
  });

export type PreferencesPatch = Partial<Preferences>;

const save = (patch: PreferencesPatch) =>
  request('/api/v1/me/preferences', preferencesSchema, { method: 'PUT', body: patch });

/**
 * Read the preferences and change them.
 *
 * The change is applied to the cache before the request goes out and rolled
 * back visibly if the server refuses it (`PREF-10`) — arranging your own
 * dashboard should never feel like waiting for permission.
 */
export function usePreferences(): {
  prefs: Preferences;
  set: (patch: PreferencesPatch) => void;
  saving: boolean;
} {
  const client = useQueryClient();
  const { data } = useQuery(preferencesQuery());

  const mutation = useMutation({
    mutationFn: save,
    onMutate: async (patch: PreferencesPatch) => {
      await client.cancelQueries({ queryKey: ['preferences'] });
      const previous = client.getQueryData<Preferences>(['preferences']);
      client.setQueryData<Preferences>(['preferences'], (old) => ({
        ...(old ?? FALLBACK),
        ...patch,
      }));
      return { previous };
    },
    onError: (_error, _patch, context) => {
      if (context?.previous) client.setQueryData(['preferences'], context.previous);
    },
    onSuccess: (fresh) => {
      // The server resolves the document through its own defaults, so what it
      // sends back can differ from what was asked for. It wins.
      client.setQueryData(['preferences'], fresh);
    },
  });

  return {
    prefs: data ?? FALLBACK,
    set: (patch) => {
      mutation.mutate(patch);
    },
    saving: mutation.isPending,
  };
}
